import React, { useCallback, useEffect, useState } from "react";
import { FileText, RotateCcw, AlertTriangle, CheckCircle2, XCircle, Clock } from "lucide-react";
import { PageHeader, Button, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type Estimate, type EstimateConfig, type EstimateMetrics } from "@/lib/api";
import { EST_STATUS, amountText, fmtDate } from "./estimates/constants";
import { EstimateManager } from "./estimates/EstimateManager";

export default function Estimates() {
  const [config, setConfig] = useState<EstimateConfig | null>(null);
  const [metrics, setMetrics] = useState<EstimateMetrics | null>(null);
  const [rows, setRows] = useState<Estimate[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(() => {
    api.estimatesList({}).then(setRows).catch((e) => toast.error(formatApiError(e)));
    api.estimatesMetrics().then(setMetrics).catch(() => {});
  }, []);
  useEffect(() => { api.estimatesConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setProcessing(true);
    try { const r = await api.estimatesProcess(); toast.success(`Processed ${r.estimates_scanned} follow-up(s), expired ${r.expired}`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setProcessing(false); }
  };

  const cards = metrics ? [
    { label: "Total estimates", value: metrics.total, icon: FileText },
    { label: "Awaiting decision", value: metrics.by_status.awaiting_decision + metrics.by_status.estimate_sent, icon: Clock },
    { label: "Accepted", value: metrics.accepted, icon: CheckCircle2 },
    { label: "Declined", value: metrics.declined, icon: XCircle },
    { label: "Win rate", value: `${metrics.win_rate}%`, icon: RotateCcw },
  ] : [];

  return (
    <div className="space-y-6" data-testid="page-estimates">
      <PageHeader title="Estimate Follow-Up" description="Track every estimate and automatically follow up with customers who haven't decided." testid="estimates-header"
        action={<Button onClick={runNow} disabled={processing} data-testid="estimates-process-btn"><RotateCcw className="h-4 w-4" /> {processing ? "Processing…" : "Run follow-up now"}</Button>} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="estimates-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          {config.followup_included ? <StatusBadge tone="green">Automated follow-up included</StatusBadge>
            : <StatusBadge tone="amber">Follow-up: upgrade to Growth+</StatusBadge>}
          {config.ai_entitled && <StatusBadge tone={config.ai_available ? "green" : "slate"}>AI drafting {config.ai_available ? "available" : "(needs LLM)"}</StatusBadge>}
          {config.followup_included && (config.sms_configured
            ? <StatusBadge tone="green">SMS live</StatusBadge>
            : <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> SMS: configuration required</StatusBadge>)}
        </div>
      )}

      {config?.followup_included && !config?.sms_configured && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="estimates-honest-note">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">Follow-up steps are scheduled automatically, but SMS sending is paused until Twilio is live. Due steps are marked <b>“Unavailable — Configuration Required”</b> and are <b>never</b> recorded as sent. They auto-execute once Twilio goes live — no rebuild needed.</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="estimates-metrics">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500"><c.icon className="h-4 w-4 text-blue-500" /> {c.label}</div>
            <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{c.value}</div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold text-slate-800">All estimates</h2>
        {rows === null ? <LoadingBlock rows={4} /> : rows.length === 0 ? (
          <EmptyState icon={FileText} title="No estimates yet" description="Add an estimate from a lead's profile to start tracking and following up." />
        ) : (
          <Card><CardContent className="overflow-x-auto p-0" data-testid="estimates-table">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Customer</th><th className="px-4 py-2.5">Amount</th>
                  <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Sent</th>
                  <th className="px-4 py-2.5">Valid until</th><th className="px-4 py-2.5">Follow-up</th>
                  <th className="px-4 py-2.5">Next follow-up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((e) => {
                  const sm = EST_STATUS[e.status] ?? EST_STATUS.estimate_requested;
                  return (
                    <tr key={e.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(e.id)} data-testid={`estimate-row-${e.id}`}>
                      <td className="px-4 py-3"><div className="font-medium text-slate-900">{e.contact_name}</div><div className="text-xs text-slate-400">{e.service ?? "—"}</div></td>
                      <td className="px-4 py-3">{amountText(e.amount, e.amount_disclosed)}</td>
                      <td className="px-4 py-3"><StatusBadge tone={sm.tone}>{sm.label}</StatusBadge></td>
                      <td className="px-4 py-3 text-slate-500">{fmtDate(e.sent_at)}</td>
                      <td className="px-4 py-3 text-slate-500">{fmtDate(e.valid_until)}</td>
                      <td className="px-4 py-3"><StatusBadge tone={e.followup_state === "active" ? "blue" : "slate"}>{(e.followup_state ?? "not_started").replace(/_/g, " ")}</StatusBadge></td>
                      <td className="px-4 py-3 text-slate-500">{fmtDate(e.next_followup_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent></Card>
        )}
      </div>

      {openId && <EstimateManager estimateId={openId} onClose={() => setOpenId(null)} onSaved={load} />}
    </div>
  );
}
